import React, { useEffect, useRef, useState } from 'react'
import { Popup, ToolbarItem } from 'devextreme-react/popup'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import { Form } from 'devextreme-react/form'
import axios from 'axios'
import notify from '../../utils/notify'
import { confirmDialog } from '../confirm-dialog/ConfirmDialog'
import { ActionForm } from './ActionForm'
import { InputTable, isInputVisible } from './InputTable'
import { PhotoViewer } from './PhotoViewer'
import type { ClickablePopupProps, PopupAction, PopupContent } from './types'
import './ClickablePopup.css'

const DEFAULT_WIDTH = 720
const VIEWER_WIDTH = 640
const POPUP_PADDING = 48

const isBlank = (value: any) => value === null || value === undefined || value === ''

const prefill = (action: PopupAction | null) =>
	Object.fromEntries(
		(action?.inputs ?? []).filter(i => i.value !== undefined && i.value !== null).map(i => [i.name, i.value])
	)

/*
 * One popup: GET HTML from POST /view/clickable (model.clickTo{Field}), plus
 * footer actions. An action with inputs first reveals the form under the HTML
 * (e.g. Reject → Confirm rejection); it does not open a second screen.
 *
 * Submit goes to POST /view/conditionalConfirm
 *   { field, action, row, inputs } → model.popupAction{Field}{Action}($row, $inputs)
 *
 * Photo thumbnails in the HTML use <a data-photo>; a click opens PhotoViewer.
 */
export default function ClickablePopup(props: ClickablePopupProps): JSX.Element {
	const { model, folder, field, caption, data, onHide, onChanged } = props

	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [content, setContent] = useState<PopupContent>({ html: '' })
	const [pendingAction, setPendingAction] = useState<PopupAction | null>(null)
	const [viewer, setViewer] = useState<{ photos: string[]; index: number } | null>(null)

	const popupRef = useRef<Popup>(null)
	const formRef = useRef<Form>(null)
	const formWrapRef = useRef<HTMLDivElement>(null)
	const formData = useRef<Record<string, any>>({})
	const submittingRef = useRef(false)

	useEffect(() => {
		let cancelled = false
		axios
			.post(`${API_FIBER_URL}/view/clickable`, { model, folder, field, data })
			.then(({ data: res }) => {
				if (cancelled) return
				const inline = (res?.actions ?? []).find((a: PopupAction) => a.inline && a.inputs?.length) ?? null
				formData.current = prefill(inline)
				setPendingAction(inline)
				setContent({ ...res, html: res?.html ?? '' })
				setLoading(false)
			})
			.catch(e => {
				if (cancelled) return
				notify(e?.response?.data?.message ?? 'Failed to load details', 'error')
				onHide()
			})
		return () => {
			cancelled = true
		}
	}, [model, folder, field, data])

	// Popup opens at loader size; repaint recenters it after content / viewer change
	useEffect(() => {
		popupRef.current?.instance.repaint()
		if (!pendingAction?.inputs?.length) return

		let inner = 0
		const outer = window.requestAnimationFrame(() => {
			inner = window.requestAnimationFrame(() => {
				const form = formWrapRef.current
				if (!form) return
				const scroller = form.closest('.dx-popup-content') as HTMLElement | null
				if (!scroller) {
					form.scrollIntoView({ behavior: 'smooth', block: 'start' })
					return
				}
				const top = form.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
				scroller.scrollTo({ top, behavior: 'smooth' })
			})
		})
		return () => {
			window.cancelAnimationFrame(outer)
			window.cancelAnimationFrame(inner)
		}
	}, [loading, viewer, pendingAction])

	const openPhoto = (e: React.MouseEvent<HTMLDivElement>) => {
		const link = (e.target as Element).closest?.('a[data-photo]') as HTMLAnchorElement | null
		if (!link) return
		e.preventDefault()
		const strip = link.closest('[data-photo-strip]') ?? link.parentElement
		const links = Array.from(strip?.querySelectorAll<HTMLAnchorElement>('a[data-photo]') ?? [link])
		setViewer({ photos: links.map(a => a.href), index: Math.max(0, links.indexOf(link)) })
	}

	const startAction = (action: PopupAction) => {
		if (action.inputs?.length && pendingAction?.name !== action.name) {
			formData.current = prefill(action)
			setPendingAction(action)
			return
		}
		void runAction(action)
	}

	const runAction = async (action: PopupAction) => {
		if (action.inputs?.length && formRef.current?.instance.validate().isValid === false) return
		if (action.layout === 'table') {
			const initial = prefill(action)
			const isChanged = (name: string) => (formData.current[name] ?? null) !== (initial[name] ?? null)
			const missing = (action.inputs ?? []).find(
				i => i.required && isInputVisible(i, formData.current) && isBlank(formData.current[i.name])
			)
			if (missing) {
				notify(`${missing.label ?? missing.name} is required`, 'error')
				return
			}
			const noReason = (action.inputs ?? []).find(
				i =>
					i.reason &&
					isInputVisible(i, formData.current) &&
					isChanged(i.name) &&
					isBlank(formData.current[i.reason.name])
			)
			if (noReason) {
				notify(`A reason is required for ${noReason.label ?? noReason.name}`, 'error')
				return
			}
		}
		if (action.confirm && !(await confirmDialog(action.confirm.title, action.confirm.message))) return
		if (submittingRef.current) return

		submittingRef.current = true
		setSubmitting(true)
		try {
			const { data: res } = await axios.post(`${API_FIBER_URL}/view/conditionalConfirm`, {
				folder,
				model,
				field,
				action: action.name,
				row: data,
				inputs: { ...formData.current },
			})
			notify(res?.message ?? `${action.text}: done`, 'success')
			onChanged?.()
			onHide()
		} catch (e: any) {
			notify(e?.response?.data?.message ?? e?.message ?? 'Request failed', 'error')
		} finally {
			submittingRef.current = false
			setSubmitting(false)
		}
	}

	const previewAction = (content.actions ?? []).find(a => a.preview && a.layout === 'table') ?? null
	const shownAction = pendingAction ?? previewAction

	const body = loading ? (
		<div className="clickable-popup-loader">
			<LoadIndicator height={60} width={60} />
		</div>
	) : (
		<>
			<div onClick={openPhoto} dangerouslySetInnerHTML={{ __html: content.html }} />
			{shownAction?.inputs?.length ? (
				<div className="clickable-popup-form">
					{shownAction.layout === 'table' && shownAction.columns ? (
						<InputTable
							key={shownAction.name + (pendingAction ? '' : ':preview')}
							inputs={shownAction.inputs ?? []}
							columns={shownAction.columns}
							formData={pendingAction ? formData.current : prefill(shownAction)}
							readOnly={!pendingAction}
						/>
					) : (
						<ActionForm ref={formRef} inputs={shownAction.inputs ?? []} formData={formData.current} />
					)}
				</div>
			) : null}
			{content.htmlAfter ? <div dangerouslySetInnerHTML={{ __html: content.htmlAfter }} /> : null}
		</>
	)

	const button = (key: string, location: 'before' | 'after', options: Record<string, any>) => (
		<ToolbarItem key={key} widget="dxButton" toolbar="bottom" location={location} options={options} />
	)

	// Must be a flat array: devextreme-react skips ToolbarItems nested in fragments
	const toolbarItems = [
		...(loading ? [] : content.actions ?? []).map(action =>
			button(action.name, action.location ?? 'before', {
				text: pendingAction?.name === action.name ? action.confirmText ?? action.text : action.text,
				type: action.type ?? 'normal',
				stylingMode: action.type === 'danger' ? 'outlined' : 'contained',
				disabled: submitting,
				onClick: () => startAction(action),
			})
		),
		button('close', 'after', { text: 'Cancel', type: 'default', stylingMode: 'contained', onClick: onHide }),
	]

	return (
		<Popup
			ref={popupRef}
			visible={true}
			dragEnabled={false}
			resizeEnabled={false}
			wrapperAttr={{ class: 'clickable-popup' }}
			showCloseButton={true}
			title={content.title ?? caption ?? field}
			width={viewer ? DEFAULT_WIDTH + VIEWER_WIDTH : DEFAULT_WIDTH}
			maxWidth="95vw"
			height="auto"
			maxHeight="90vh"
			position="center"
			hideOnOutsideClick={!submitting}
			onHiding={onHide}
			contentRender={() => (
				<div className="clickable-popup-layout">
					<div
						className="clickable-popup-body"
						style={viewer ? { flex: `0 0 ${DEFAULT_WIDTH - POPUP_PADDING}px` } : undefined}
					>
						{body}
					</div>
					{viewer && (
						<PhotoViewer
							photos={viewer.photos}
							index={viewer.index}
							onIndexChange={index => setViewer(v => (v ? { ...v, index } : v))}
							onClose={() => setViewer(null)}
						/>
					)}
				</div>
			)}
		>
			{toolbarItems}
		</Popup>
	)
}
